import React, { useEffect, useState } from "react";
import { Download, Share, X, Plus, ChevronRight } from "lucide-react";

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const InstallPrompt = ({ hideLauncher = false }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    const h = () => handleTap();
    window.addEventListener("zanitel-install-request", h);
    return () => window.removeEventListener("zanitel-install-request", h);
  });

  const handleTap = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
      return;
    }
    setShowGuide(true);
  };

  if (installed) return null;

  const ios = isIos();

  return (
    <>
      {!hideLauncher && (
        <button
          onClick={handleTap}
          className="fixed bottom-[8.5rem] left-4 z-[65] h-12 w-12 rounded-full bg-zinc-900 shadow-2xl flex items-center justify-center md:hidden active:scale-95 transition-transform"
          data-testid="pwa-install-banner"
          aria-label="Install the ZEEPTEL app"
        >
          <img src="/icons/icon-192.png" alt="" className="h-8 w-8 rounded-full" />
          <span className="absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full bg-primary flex items-center justify-center border-2 border-white" data-testid="pwa-install-button">
            <Download className="h-2.5 w-2.5 text-white" />
          </span>
        </button>
      )}

      {showGuide && (
        <div className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="pwa-install-guide" onClick={() => setShowGuide(false)}>
          <div className="w-full max-w-md bg-white rounded-t-3xl md:rounded-3xl p-6 pb-10 md:pb-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <img src="/icons/icon-192.png" alt="" className="h-10 w-10 rounded-xl" />
                <div>
                  <div className="font-heading font-bold">Install ZEEPTEL</div>
                  <div className="text-xs text-zinc-500">Free — takes 10 seconds, no app store</div>
                </div>
              </div>
              <button onClick={() => setShowGuide(false)} className="text-zinc-400 p-1" data-testid="pwa-guide-close" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {ios ? (
              <ol className="space-y-4 text-sm">
                <li className="flex items-center gap-3">
                  <span className="h-7 w-7 shrink-0 rounded-full bg-primary text-white font-bold flex items-center justify-center text-xs">1</span>
                  <span className="flex items-center gap-1.5 flex-wrap">Tap the <Share className="h-4 w-4 text-primary inline" /> <b>Share</b> button at the bottom of Safari</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-7 w-7 shrink-0 rounded-full bg-primary text-white font-bold flex items-center justify-center text-xs">2</span>
                  <span className="flex items-center gap-1.5 flex-wrap">Scroll down and tap <Plus className="h-4 w-4 text-primary inline" /> <b>Add to Home Screen</b></span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-7 w-7 shrink-0 rounded-full bg-primary text-white font-bold flex items-center justify-center text-xs">3</span>
                  <span>Tap <b>Add</b> — ZEEPTEL appears on your home screen like a real app</span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-4 text-sm">
                <li className="flex items-center gap-3">
                  <span className="h-7 w-7 shrink-0 rounded-full bg-primary text-white font-bold flex items-center justify-center text-xs">1</span>
                  <span>Open the browser menu (<b>⋮</b> top right of Chrome) — or on computer, click the <b>install icon</b> at the right end of the address bar</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-7 w-7 shrink-0 rounded-full bg-primary text-white font-bold flex items-center justify-center text-xs">2</span>
                  <span className="flex items-center gap-1.5 flex-wrap">Tap <b>Add to Home screen</b> <ChevronRight className="h-4 w-4 text-zinc-400" /> <b>Install</b></span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="h-7 w-7 shrink-0 rounded-full bg-primary text-white font-bold flex items-center justify-center text-xs">3</span>
                  <span>ZEEPTEL appears on your home screen like a real app</span>
                </li>
              </ol>
            )}
          </div>
        </div>
      )}
    </>
  );
};
